import React, { useState } from 'react';
import type { FC } from 'react';
import { FormattedMessage, useIntl } from 'react-intl-next';

import type { ADFEntity } from '@atlaskit/adf-utils/types';
import type { DocNode } from '@atlaskit/adf-schema';
import type { PublicPluginAPI } from '@atlaskit/editor-common/types';
import type { QuickInsertPlugin } from '@atlaskit/editor-plugin-quick-insert';
import { useAnalyticsEvents } from '@atlaskit/analytics-next';
import ButtonGroup from '@atlaskit/button/button-group';
import Button from '@atlaskit/button/new';
import { cssMap } from '@atlaskit/css';
import Form, {
	Field,
	FormFooter,
	CharacterCounterField,
	ErrorMessage,
	MessageWrapper,
} from '@atlaskit/form';
import Heading from '@atlaskit/heading';
import { CloseButton, ModalBody, useModal } from '@atlaskit/modal-dialog';
import { Box, Inline, Stack } from '@atlaskit/primitives/compiled';
import Textfield from '@atlaskit/textfield';
import { token } from '@atlaskit/tokens';
import TextArea from '@atlaskit/textarea';

import { useChoreographerAssets } from '@confluence/choreographer-services/entry-points/useChoreographerAssets.ts';
import { withFlags, type WithFlagsProps } from '@confluence/flags/entry-points/withFlags.ts';

import { SnippetEditorModalMode, type Snippet } from '../snippetTypes.ts';
import type { CreateSnippetOptions } from '../store/actions/createActions.ts';
import { useSnippetsActions } from '../store/snippetsStore.ts';
import { getSuggestedDuplicateName } from '../snippetUtils.ts';
import { SnippetEditor } from '../editor/SnippetEditor.tsx';

import { messages } from './SnippetEditorModal.i18n.ts';

const EMPTY_ADF = { type: 'doc' as const, version: 1 as const, content: [] };

export const MAX_SNIPPET_NAME_LENGTH = 60;
export const MAX_SNIPPET_DESCRIPTION_LENGTH = 120;

export enum SnippetEditorModalEvents {
	CANCELLED = 'snippetEditorModalCancelled',
	CLOSED = 'snippetEditorModalClosed',
	CREATED = 'snippetEditorModalCreated',
	DUPLICATED = 'snippetEditorModalDuplicated',
	EDITED = 'snippetEditorModalEdited',
}

type SnippetEditorModalBaseProps = {
	mode: SnippetEditorModalMode;
	onClose: () => void;
	onCloseComplete: () => void;
	adf?: ADFEntity | ADFEntity[];
	snippet?: Snippet;
	contentId: string;
	spaceId: string;
	spaceKey: string;
	editorAPI?: PublicPluginAPI<[QuickInsertPlugin]> | null;
};

export type SnippetEditorModalProps = SnippetEditorModalBaseProps & WithFlagsProps;

interface SnippetEditorModalFormValues {
	snippetName: string;
	snippetDescription: string;
}

const styles = cssMap({
	modalContent: {
		backgroundColor: token('elevation.surface.sunken'),
		borderRadius: token('radius.xlarge'),
	},
	/** Fills ModalBody; establishes height so bodyContainer (and thus preview column) get 100% height. */
	bodyWrapper: {
		display: 'flex',
		flex: 1,
		height: '80vh',
		overflow: 'hidden',
	},
	/** Two-column row; padding so column box-shadows are not clipped by overflow. */
	bodyContainer: {
		display: 'flex',
		flexDirection: 'row',
		gap: token('space.200'),
		flex: 1,
		overflow: 'hidden',
		paddingInline: token('space.050'),
		paddingTop: token('space.150'),
		paddingBottom: token('space.200'),
		boxSizing: 'border-box',
	},
	headerContainer: {
		paddingTop: token('space.150'),
		paddingLeft: token('space.400'),
		paddingRight: token('space.300'),
		flexShrink: 0,
	},
	previewColumn: {
		flex: 1,
		height: '100%',
		borderRadius: token('radius.xlarge'),
		backgroundColor: token('elevation.surface'),
		boxShadow: token('elevation.shadow.raised'),
		transition: 'box-shadow 300ms ease-out',
		overflow: 'auto',
	},
	formColumn: {
		width: '320px',
		minWidth: '320px',
		flexShrink: 0,
		borderRadius: token('radius.xlarge'),
		backgroundColor: token('elevation.surface'),
		boxShadow: token('elevation.shadow.raised'),
		transition: 'box-shadow 300ms ease-out',
		padding: token('space.300'),
		display: 'flex',
		flexDirection: 'column',
		overflow: 'hidden',
	},
	formContainer: {
		display: 'flex',
		flexDirection: 'column',
		flex: 1,
	},
	formFields: {
		flex: 1,
	},
	formActions: {
		flexShrink: 0,
		paddingTop: token('space.200'),
	},
});

// TODO: remove this function when start using the editor instead of the renderer
// For ADFRenderer to work, we need to normalize the ADF to a doc node and saved snippet body maybe not always a doc node
const normalizeToDoc = (
	adf: ADFEntity | ADFEntity[] | undefined,
): { type: 'doc'; version: 1; content: any[] } => {
	if (!adf) {
		return EMPTY_ADF;
	}
	if (Array.isArray(adf)) {
		return { type: 'doc', version: 1, content: adf };
	}
	if (adf.type === 'doc' && Array.isArray(adf.content)) {
		return { type: 'doc', version: 1, content: adf.content };
	}
	return { type: 'doc', version: 1, content: [adf] };
};

const getPreviewAdf = (
	mode: SnippetEditorModalMode,
	snippet: Snippet | undefined,
	adf?: ADFEntity | ADFEntity[],
): { type: 'doc'; version: 1; content: any[] } => {
	const isEditingOrDuplicating =
		mode === SnippetEditorModalMode.EDIT || mode === SnippetEditorModalMode.DUPLICATE;
	const source = isEditingOrDuplicating && snippet?.body ? snippet.body : adf;
	return normalizeToDoc(source);
};

const getDefaultFormValues = (
	mode: SnippetEditorModalMode,
	snippet: Snippet | undefined,
): SnippetEditorModalFormValues => {
	if (mode === SnippetEditorModalMode.EDIT && snippet) {
		return {
			snippetName: snippet.name ?? '',
			snippetDescription: snippet.description ?? '',
		};
	}
	if (mode === SnippetEditorModalMode.DUPLICATE && snippet) {
		return {
			snippetName: getSuggestedDuplicateName(snippet.name ?? ''),
			snippetDescription: snippet.description ?? '',
		};
	} /* SnippetEditorModalMode.CREATE */
	return { snippetName: '', snippetDescription: '' };
};

const getModalTitle = (
	mode: SnippetEditorModalMode,
	snippet: Snippet | undefined,
): React.ReactNode => {
	if (mode === SnippetEditorModalMode.EDIT) {
		return <FormattedMessage {...messages.snippetEditorModalTitleEdit} />;
	}
	if (mode === SnippetEditorModalMode.DUPLICATE) {
		return (
			<FormattedMessage
				{...messages.snippetEditorModalTitleDuplicate}
				values={{ snippetName: snippet?.name ?? '' }}
			/>
		);
	}
	return <FormattedMessage {...messages.snippetEditorModalTitleCreate} />;
};

const getSubmitLabel = (mode: SnippetEditorModalMode): React.ReactNode => {
	if (mode === SnippetEditorModalMode.EDIT) {
		return <FormattedMessage {...messages.snippetEditorModalSaveButton} />;
	}
	if (mode === SnippetEditorModalMode.DUPLICATE) {
		return <FormattedMessage {...messages.snippetEditorModalDuplicateButton} />;
	}
	return <FormattedMessage {...messages.snippetEditorModalCreateButton} />;
};

const getFormKey = (mode: SnippetEditorModalMode, snippet: Snippet | undefined): string => {
	if (mode === SnippetEditorModalMode.EDIT) {
		return snippet ? snippet.snippetId : SnippetEditorModalMode.CREATE;
	}
	if (mode === SnippetEditorModalMode.DUPLICATE) {
		return snippet ? `duplicate-${snippet.snippetId}` : SnippetEditorModalMode.CREATE;
	}
	return SnippetEditorModalMode.CREATE;
};

const SnippetEditorModalHeader = ({
	title,
	onClose,
}: {
	title: React.ReactNode;
	onClose: () => void;
}) => {
	const { titleId } = useModal();
	return (
		<Inline
			alignInline="center"
			spread="space-between"
			alignBlock="center"
			xcss={styles.headerContainer}
		>
			<Heading size="small" id={titleId}>
				{title}
			</Heading>
			<CloseButton onClick={onClose} />
		</Inline>
	);
};

const SnippetEditorModalWithFlags: FC<SnippetEditorModalProps> = ({
	mode,
	adf,
	snippet,
	onClose,
	onCloseComplete,
	flags,
	contentId,
	spaceId,
	spaceKey,
	editorAPI,
}) => {
	const initialAdf = getPreviewAdf(mode, snippet, adf);
	const [currentAdf, setCurrentAdf] = useState<DocNode>(initialAdf);
	const [adfError, setADFError] = useState<any>(null);
	const defaultValues = getDefaultFormValues(mode, snippet);

	const { createAnalyticsEvent } = useAnalyticsEvents();
	const { Modal } = useChoreographerAssets();
	const intl = useIntl();
	const snippetsActions = useSnippetsActions();

	const fireSnippetEditorModalAnalytics = (event: SnippetEditorModalEvents) => {
		createAnalyticsEvent({
			type: 'sendUIEvent',
			data: {
				source: 'confluenceSnippetEditorModal',
				action: 'closed',
				actionSubject: 'button',
				actionSubjectId: 'snippetEditor',
				type: 'ui',
				attributes: {
					event,
				},
			},
		}).fire();
	};

	const handleADFError = (error: any) => {
		setADFError(error);
	};

	const handleDocumentChange = (doc: DocNode) => {
		setCurrentAdf(doc);
	};

	const handleClose = (event: SnippetEditorModalEvents) => {
		fireSnippetEditorModalAnalytics(event);
		onClose();
	};

	const submit = async (values: SnippetEditorModalFormValues) => {
		if (!flags) {
			return;
		}

		// The actions handle errors when options are provided: it shows an error flag and
		// returns null (it does not throw).
		// Only close the modal on success so the user can retry on failure.
		if (mode === SnippetEditorModalMode.CREATE) {
			const newSnippet = await snippetsActions.createSnippet(
				values.snippetName,
				currentAdf,
				values.snippetDescription,
				{
					flags,
					intl,
					editorAPI: editorAPI ?? undefined,
				} as CreateSnippetOptions,
			);
			if (newSnippet) {
				handleClose(SnippetEditorModalEvents.CREATED);
			}
		} else if (mode === SnippetEditorModalMode.DUPLICATE) {
			if (!snippet) {
				return;
			}
			const duplicatedSnippet = await snippetsActions.duplicateSnippet(
				values.snippetName,
				currentAdf,
				values.snippetDescription,
				{
					flags,
					intl,
					editorAPI: editorAPI ?? undefined,
				},
			);
			if (duplicatedSnippet) {
				handleClose(SnippetEditorModalEvents.DUPLICATED);
			}
		} else {
			/* SnippetEditorModalMode.EDIT */
			if (!snippet) {
				return;
			}
			const updatedSnippet = await snippetsActions.updateSnippet(
				snippet.snippetId,
				values.snippetName,
				currentAdf,
				values.snippetDescription,
				{
					flags,
					intl,
					editorAPI: editorAPI ?? undefined,
				},
			);
			if (updatedSnippet) {
				handleClose(SnippetEditorModalEvents.EDITED);
			}
		}
	};

	const title = getModalTitle(mode, snippet);
	const submitLabel = getSubmitLabel(mode);
	const formKey = getFormKey(mode, snippet);

	return (
		<Modal
			width="1400px"
			onClose={() => handleClose(SnippetEditorModalEvents.CLOSED)}
			onCloseComplete={onCloseComplete}
		>
			<Box xcss={styles.modalContent}>
				<SnippetEditorModalHeader
					title={title}
					onClose={() => handleClose(SnippetEditorModalEvents.CLOSED)}
				/>
				<Form<SnippetEditorModalFormValues> key={formKey} noValidate onSubmit={submit}>
					{({ submitting, formProps }) => (
						<form {...formProps} css={styles.formContainer}>
							<ModalBody>
								<Box xcss={styles.bodyWrapper}>
									<Box xcss={styles.bodyContainer}>
										<Box xcss={styles.previewColumn}>
											<SnippetEditor
												defaultValue={initialAdf}
												onDocumentChange={handleDocumentChange}
												onError={handleADFError}
												flags={flags}
												contentId={contentId}
												spaceId={spaceId}
												spaceKey={spaceKey}
											/>
										</Box>
										<Box xcss={styles.formColumn}>
											<Stack space="space.300" xcss={styles.formFields}>
												<Field
													name="snippetName"
													label={<FormattedMessage {...messages.snippetEditorModalNameLabel} />}
													defaultValue={defaultValues.snippetName}
													component={({ fieldProps }) => (
														<Textfield
															{...fieldProps}
															maxLength={MAX_SNIPPET_NAME_LENGTH}
															placeholder={intl.formatMessage(
																messages.snippetEditorModalNamePlaceholder,
															)}
														/>
													)}
													validate={(value) =>
														value
															? undefined
															: intl.formatMessage(messages.snippetEditorModalNameError)
													}
													isRequired
												/>
												<CharacterCounterField<string, HTMLTextAreaElement>
													name="snippetDescription"
													label={
														<FormattedMessage {...messages.snippetEditorModalDescriptionLabel} />
													}
													defaultValue={defaultValues.snippetDescription}
													maxCharacters={MAX_SNIPPET_DESCRIPTION_LENGTH}
												>
													{({ fieldProps }) => (
														<TextArea
															{...fieldProps}
															placeholder={intl.formatMessage(
																messages.snippetEditorModalDescriptionPlaceholder,
															)}
														/>
													)}
												</CharacterCounterField>
												<Field
													name="snippetBody"
													defaultValue={adfError}
													validate={() =>
														adfError
															? intl.formatMessage(messages.snippetEditorModalAdfError)
															: undefined
													}
												>
													{({ error }) => (
														<MessageWrapper>
															{error && <ErrorMessage>{error}</ErrorMessage>}
														</MessageWrapper>
													)}
												</Field>
											</Stack>
											<Box xcss={styles.formActions}>
												<FormFooter align="end">
													<ButtonGroup>
														<Button
															appearance="subtle"
															onClick={() => handleClose(SnippetEditorModalEvents.CANCELLED)}
														>
															<FormattedMessage {...messages.snippetEditorModalCancelButton} />
														</Button>
														<Button type="submit" appearance="primary" isLoading={submitting}>
															{submitLabel}
														</Button>
													</ButtonGroup>
												</FormFooter>
											</Box>
										</Box>
									</Box>
								</Box>
							</ModalBody>
						</form>
					)}
				</Form>
			</Box>
		</Modal>
	);
};

export const SnippetEditorModal = withFlags(SnippetEditorModalWithFlags);
